// src/components/EntrepreneurCard/EntrepreneurCard.js
import React, { useState, useEffect } from 'react';
import LocationIcon from '../../assets/ico-km-distancia.svg';
import Button from '../Button/Button';
import H3 from '../H3/H3';
import Avatar from '../Avatar/Avatar';
import {
  normalizeUrl,
  isValidUrl,
  formatUrlForDisplay,
  normalizeInstagramUrl,
  extractInstagramHandle,
  formatPhoneForTel,
  formatPhoneForDisplay,
} from '../../utils/contactFormatters';

// Import the necessary Firebase functions
import { getAuth } from 'firebase/auth';
import { collection, addDoc, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

// Import your heart icons
import heartIconOver from '../../assets/ico-coracao-over.svg';
import heartIcon from '../../assets/ico-favorito.svg';

const categoryNamesWithAccent = {
  moradia: 'Moradia',
  alimentacao: 'Alimentação',
  trocas: 'Trocas',
  transporte: 'Transporte',
  documentacao: 'Documentação',
  emprego: 'Emprego',
  comunicacao: 'Comunicação',
  treinamento: 'Treinamento',
  saude: 'Saúde',
  outros: 'Outros',
};

const sanitizeAboutBusiness = (text) =>
  text ? text.replace(/https?:\/\/\S+/g, '[link removido]') : '';

const ContactLinks = ({ entrepreneur }) => {
  const websiteHref = isValidUrl(entrepreneur.website) ? normalizeUrl(entrepreneur.website) : '';
  const websiteLabel = websiteHref ? formatUrlForDisplay(websiteHref) : '';
  const instagramHandle = extractInstagramHandle(entrepreneur.instagram);
  const instagramHref = instagramHandle ? normalizeInstagramUrl(entrepreneur.instagram) : '';
  const phoneHref = formatPhoneForTel(entrepreneur.businessPhone);
  const phoneLabel = formatPhoneForDisplay(entrepreneur.businessPhone);

  if (!websiteHref && !instagramHref && !phoneHref) return null;

  return (
    <div className="flex flex-col mt-4 space-y-2">
      {websiteHref && (
        <a
          href={websiteHref}
          className="flex items-center text-blue-800 hover:text-blue-700 break-all"
          target="_blank"
          rel="noopener noreferrer"
          title={websiteHref}
        >
          <img src="/img/ico-url-perfil.svg" alt="" className="mr-2 w-5 h-5 flex-shrink-0" />
          <span className="truncate">{websiteLabel}</span>
        </a>
      )}
      {instagramHref && (
        <a
          href={instagramHref}
          className="flex items-center text-blue-800 hover:text-blue-700"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src="/img/ico-instagram-perfil.svg" alt="" className="mr-2 w-5 h-5 flex-shrink-0" />
          <span className="truncate">@{instagramHandle}</span>
        </a>
      )}
      {phoneHref && (
        <a
          href={`tel:${phoneHref}`}
          className="flex items-center text-blue-800 hover:text-blue-700"
        >
          <img src="/img/ico-telefone-perfil.svg" alt="" className="mr-2 w-5 h-5 flex-shrink-0" />
          <span>{phoneLabel}</span>
        </a>
      )}
    </div>
  );
};

const EntrepreneurCard = ({ entrepreneur, onShowDetails }) => {
  const auth = getAuth();
  const currentUser = auth.currentUser;

  const [isFavorite, setIsFavorite] = useState(false);
  const [isHeartHovered, setIsHeartHovered] = useState(false);

  useEffect(() => {
    const checkIfFavorite = async () => {
      if (currentUser) {
        const favRef = collection(db, "fav_entrepreneur");
        const q = query(favRef, where("userId", "==", currentUser.uid), where("entrepreneurId", "==", entrepreneur.id));
        const snapshot = await getDocs(q);
        setIsFavorite(!snapshot.empty);
      }
    };
    checkIfFavorite();
  }, [currentUser, entrepreneur.id]);

  const toggleFavorite = async () => {
    const favRef = collection(db, "fav_entrepreneur");
    const q = query(favRef, where("userId", "==", currentUser.uid), where("entrepreneurId", "==", entrepreneur.id));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      await deleteDoc(snapshot.docs[0].ref);
      setIsFavorite(false);
    } else {
      await addDoc(favRef, { userId: currentUser.uid, entrepreneurId: entrepreneur.id });
      setIsFavorite(true);
    }
  };

  const categoryList = Array.isArray(entrepreneur.categories) && entrepreneur.categories.length > 0
    ? entrepreneur.categories
    : (entrepreneur.category ? [entrepreneur.category] : []);
  const isHighlighted = entrepreneur.profileHighlight === '1';

  return (
    <div className={`h-450 bg-white rounded-2xl p-5 gap-4 relative ${isHighlighted ? 'ring-2 ring-orange-400' : ''}`}>
      {isHighlighted && (
        <span className="absolute -top-2 -left-2 bg-orange-500 text-white text-xs font-bold uppercase px-2 py-1 rounded-full shadow">
          Destaque
        </span>
      )}
      <H3 text={entrepreneur.companyName} textColor="purple" />
      {categoryList.length > 0 && (
        <div className="flex flex-wrap gap-1 -mt-6 mb-2">
          {categoryList.map((cat) => (
            <span
              key={cat}
              className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200"
            >
              {categoryNamesWithAccent[cat] || cat}
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center mt-1">
        <img src={LocationIcon} alt="Location" className="w-4 h-4 mr-2" />
        <h4 className="text-base text-gray-500">{entrepreneur.businessCity} - {entrepreneur.businessState}</h4>
      </div>
      <div className="flex items-center mt-4">
        <Avatar src={entrepreneur.userAvatar} alt="User Avatar" name={entrepreneur.userName} />
        <h5 className="text-base text-gray-500">Por {entrepreneur.userName}</h5>
      </div>
      <p className="text-base mt-4 text-gray-600">{sanitizeAboutBusiness(entrepreneur.aboutBusiness)}</p>

      <ContactLinks entrepreneur={entrepreneur} />

      <div className="absolute bottom-7 left-2.5 right-2.5 flex justify-center">
        <Button text="Ver mais" variant="transparent" onClick={() => onShowDetails(entrepreneur)} />
      </div>

      {!currentUser || currentUser.uid !== entrepreneur.userId ? (
        <button
          className="absolute top-10 right-5 w-6 h-6 flex items-center justify-center"
          onMouseEnter={() => setIsHeartHovered(true)}
          onMouseLeave={() => setIsHeartHovered(false)}
          onClick={toggleFavorite}
        >
          <img
            src={isHeartHovered || isFavorite ? heartIconOver : heartIcon}
            alt="Favoritar"
            className="w-6 h-6"
          />
        </button>
      ) : null}
    </div>
  );
};

export default EntrepreneurCard;
